import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../redux/store";
import { getTimeOffRequestList, setSelectedTimeoffForView, clearSelectedTimeoffForView } from "../../redux/features/timeoff";
import { Timeoff } from "../../types";
import { subDays, addDays, eachDayOfInterval, format } from "date-fns";
import { CalendarDays, Clock, CheckCircle, XCircle, Info, ArrowLeft } from "lucide-react";
import { DateTimeUtil } from "../../utils";
import { useNavigate } from "react-router";

const statusColors: Record<string, string> = {
    Approved: "bg-green-100 text-green-600 border-green-300",
    Pending: "bg-yellow-100 text-yellow-600 border-yellow-300",
    Rejected: "bg-red-100 text-red-600 border-red-300",
    Cancelled: "bg-gray-100 text-gray-600 border-gray-300",
};

const getStatusIcon = (status: string) => {
    switch (status) {
        case "Approved":
            return <CheckCircle className="w-5 h-5 text-green-600" />;
        case "Pending":
            return <Clock className="w-5 h-5 text-yellow-600" />;
        case "Rejected":
            return <XCircle className="w-5 h-5 text-red-600" />;
        default:
            return <Info className="w-5 h-5 text-gray-600" />;
    }
};

const getTimeBoundaries = (selectedTimeoff: Timeoff) => {
    const { details_json } = selectedTimeoff;

    if (!details_json || !details_json.days || details_json.days.length === 0) {
        return {
            formattedRange: DateTimeUtil.formatDateTimeRange(selectedTimeoff.start_date, selectedTimeoff.end_date),
        };
    }

    const firstDay = details_json.days[0];
    const lastDay = details_json.days[details_json.days.length - 1];

    const fullStart = `${selectedTimeoff.start_date} ${firstDay.start_time ?? "00:00:00"}`;
    const fullEnd = `${selectedTimeoff.end_date} ${lastDay.end_time ?? "23:59:59"}`;

    return {
        formattedRange: DateTimeUtil.formatDateTimeRangeUtc(fullStart, fullEnd),
    };
};

const ViewAllTimeoff: React.FC = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { fetchedTimeoffRequests, isLoadingTimeOffRequests, selectedTimeoffForView } = useSelector((state: RootState) => state.timeoff);
    const user = useSelector((state: RootState) => state.user.user);

    useEffect(() => {
        if (user?.id) {
            const dateRange = getDateRange();
            dispatch(getTimeOffRequestList({ start_date: dateRange[0], end_date: dateRange[dateRange.length - 1], employee_id: user.id.toString(), category_code: "" }));
        }
    }, [user]);

    const getDateRange = (): string[] => {
        const today = new Date();
        const start = subDays(today, 30);
        const end = addDays(today, 30);
        return eachDayOfInterval({ start, end }).map((date) => format(date, "yyyy-MM-dd"));
    };

    const handleNavBack = () => {
        dispatch(clearSelectedTimeoffForView());
        navigate(-1);
    };

    return (
        <div className="flex h-screen">
            {/* Left Pane - List of Time-Off Requests */}
            <div className="w-1/3 bg-gray-50 border-r overflow-y-auto p-4">
                <div className="flex justify-between items-center mb-4">
                    <button onClick={handleNavBack} className="p-2 rounded-md hover:bg-gray-100 transition">
                        <ArrowLeft className="w-6 h-6 text-gray-500" />
                    </button>
                    <h2 className="text-lg font-semibold text-gray-700">All Time Off Requests</h2>
                </div>

                {isLoadingTimeOffRequests ? (
                    <p className="text-gray-500">Loading time-off requests...</p>
                ) : fetchedTimeoffRequests.length === 0 ? (
                    <p className="text-gray-500">No time-off requests found.</p>
                ) : (
                    <ul className="space-y-3">
                        {fetchedTimeoffRequests.map((request) => (
                            <li
                                key={request.time_off_request_id}
                                className={`p-3 flex items-center justify-between border rounded-md cursor-pointer 
                                    ${selectedTimeoffForView?.time_off_request_id === request.time_off_request_id ? "bg-blue-100" : "bg-white hover:bg-gray-100"}
                                `}
                                onClick={() => dispatch(setSelectedTimeoffForView(request))}
                            >
                                <div className="flex items-center space-x-3">
                                    <CalendarDays className="text-blue-500 w-5 h-5" />
                                    <div>
                                        <p className="text-sm font-medium text-gray-900">
                                            {format(new Date(request.start_date!), "MMM d")} - {format(new Date(request.end_date!), "MMM d")}
                                        </p>
                                        <p className="text-xs text-gray-600">{request.category_code}</p>
                                    </div>
                                </div>
                                <span className="text-gray-700 text-sm font-semibold">{request.hours_requested}h</span>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            {/* Right Pane - Time-Off Details */}
            <div className="w-2/3 bg-white p-6">
                {selectedTimeoffForView ? (
                    <div className="w-full max-w-screen mx-auto bg-white rounded-lg py-12">
                        {/* Header */}
                        <div className="mb-6">
                            <h2 className="text-2xl font-semibold text-gray-800">Time Off Request Details</h2>
                            <p className="mt-2 text-gray-600 text-lg italic">Reason: {selectedTimeoffForView.reason || "No reason provided"}</p>
                        </div>

                        {/* Summary Section */}
                        <table className="table-auto text-gray-700 border-collapse">
                            <tbody>
                                <tr className="border-b border-gray-300">
                                    <td className="py-2 font-medium pr-4">Dates:</td>
                                    <td className="py-2 text-gray-900 text-right pl-4">{getTimeBoundaries(selectedTimeoffForView).formattedRange}</td>
                                </tr>
                                <tr className="border-b border-gray-300">
                                    <td className="py-2 font-medium pr-4">Category:</td>
                                    <td className="py-2 text-gray-900 text-right pl-4">{selectedTimeoffForView.category_code || "N/A"}</td>
                                </tr>
                                <tr className="border-b border-gray-300">
                                    <td className="py-2 font-medium pr-4">Hours Requested:</td>
                                    <td className="py-2 text-gray-900 text-right pl-4">{selectedTimeoffForView.hours_requested || "0"}h</td>
                                </tr>
                                <tr className="border-b border-gray-300">
                                    <td className="py-2 font-medium pr-4">Status:</td>
                                    <td className="py-2 text-right pl-4">
                                        <span className={`inline-flex items-center px-3 py-1 text-sm font-medium ${statusColors[selectedTimeoffForView.status || "Pending"]}`}>
                                            {getStatusIcon(selectedTimeoffForView.status || "Pending")}
                                            <span className="ml-2">{selectedTimeoffForView.status || "Pending"}</span>
                                        </span>
                                    </td>
                                </tr>
                            </tbody>
                        </table>

                        {/* Details Breakdown */}
                        <div className="mt-6 h-64 overflow-y-auto">
                            <h3 className="text-lg font-semibold text-gray-800 mb-3">Request Breakdown</h3>
                            <table className="w-full max-w-5xl text-sm text-gray-600">
                                <thead className="border-b bg-gray-200">
                                    <tr>
                                        <th className="py-2 px-3 text-left">Date</th>
                                        <th className="py-2 px-3 text-left">Start Time</th>
                                        <th className="py-2 px-3 text-left">End Time</th>
                                        <th className="py-2 px-3 text-left">Hours</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {selectedTimeoffForView.details_json?.days.map((day, i) => {
                                        const fullStartTime = `${day.date} ${day.start_time}`;
                                        const fullEndTime = `${day.date} ${day.end_time}`;

                                        // Calculate amount dynamically if not provided
                                        const calculatedAmount = day.amount ?? DateTimeUtil.getDuration(fullStartTime, fullEndTime);
                                        return (
                                            <tr key={i} className="border-b">
                                                <td className="py-2 px-3">{DateTimeUtil.formatDate(day.date, "MMM dd, yyyy")}</td>
                                                <td className="py-2 px-3">{day.start_time}</td>
                                                <td className="py-2 px-3">{day.end_time}</td>
                                                <td className="py-2 px-3">{calculatedAmount}h</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                ) : (
                    <p className="text-center text-gray-600 py-6">Select a time-off request to view details.</p>
                )}
            </div>
        </div>
    );
};

export default ViewAllTimeoff;
